'use client';

/**
 * EditorialActionBanner — account action-required banners for the SE3 editorial
 * shell (verify identity, verify email).
 *
 * SE3 renders its own EditorialNav and deliberately skips the control
 * LayoutWrapper → StyledTopNav → SETopNav chrome. That chrome is where the
 * account action banners live, so they were missing on the SE3 test experience
 * while control showed them (Paige QA 2026-07-15).
 *
 * This reuses the EXACT control `BannerCarousel` — so the "which banner shows
 * for which account status" logic (the compliance-sensitive part) can never
 * drift from control — and only:
 *   1. wires it from the same data control uses (redux `userAccounts` populated
 *      by `useGetUser`, plus the user's email-confirmed flag), and
 *   2. neutralises the carousel's `position: fixed` so it flows in-document
 *      above the sticky editorial nav instead of overlaying (and hiding) it.
 *
 * Promotional Strapi banners are intentionally NOT passed here — SE3 renders
 * those through its own surfaces (e.g. EditorialExploreBanner); mixing them in
 * would double up the promo banner.
 */

import { useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from 'utils/errorTracking';
import { styled } from '@mui/material/styles';
import BannerCarouselRaw from '@startengine/design-system.ui.top-nav/dist/components/bannerCarousel';
import useGetUser from 'hooks/useGetUser';
import { trackEvent } from 'utils/analytics';
import { investUrl, secondaryUrl } from 'utils/links';

interface InvestmentAccountLike {
  accountStatus?: string;
  custodianAccountStatus?: string;
  custodianAccountStatusDetail?: Record<string, unknown> | null;
}

interface BannerCarouselProps {
  investmentAccount?: InvestmentAccountLike;
  emailConfirmed?: boolean;
  userEmail?: string;
  primaryUserId?: string | null;
  strapiBannerData?: unknown;
  onAccountAction?: () => void;
}

// The published design-system carousel is `@ts-nocheck` JS whose generated
// `.d.ts` types every prop as `null | undefined`; cast to a real prop shape the
// same way the other SE3 design-system adapters do (see FilterSidebar /
// AnnouncementBanner).
const BannerCarousel = BannerCarouselRaw as unknown as FC<BannerCarouselProps>;

const HELP_URL = 'https://help.startengine.com/contact/contact-us-ByDplTJXF';

const captureTrackingError = (e: unknown) =>
  Sentry.captureException(e, { tags: { area: 'editorial_action_banner' } });

// Anchor the carousel's absolutely-positioned arrows/dots to this wrapper and
// override its fixed positioning so the banner participates in normal flow —
// pushing the sticky nav and page content down rather than covering them.
const InFlow = styled('div')({
  position: 'relative',
  '& [data-cy="banner-carousel"]': {
    position: 'static',
  },
});

const selectUserAccounts = (state: {
  user?: { userAccounts?: InvestmentAccountLike[] };
}): InvestmentAccountLike[] | undefined => state?.user?.userAccounts;

export default function EditorialActionBanner() {
  const { data: user } = useGetUser();
  const investmentAccounts = useSelector(selectUserAccounts);

  const investmentAccount = useMemo(
    () => investmentAccounts?.find((account) => account.accountStatus !== 'CLOSED'),
    [investmentAccounts],
  );

  const typedUser = user as { email?: string; primaryUserId?: string; emailConfirmed?: boolean };
  // Only nag when the API explicitly reports an unconfirmed email. Anonymous,
  // still-resolving, and payloads that simply omit the field must all stay
  // "confirmed" — control (`StyledTopNav.checkEmailConfirmed`) passes
  // `undefined` and lets the design-system default apply, so coercing unknown
  // into `false` here showed the banner on surfaces control left clean.
  const emailConfirmed = typedUser?.emailConfirmed !== false;

  // Route the banner CTA exactly as control's StyledTopNav handleAccountAction.
  // The banner copy itself comes from the locked design-system carousel, so the
  // CTA click is instrumented here at the wrapper — on every branch, so a
  // blocked account bouncing off the help centre is measurable rather than only
  // visible in a session recording. `path` is read at click time because this
  // component renders on the homepage, explore, and private surfaces.
  const handleAccountAction = () => {
    const activeAccount = investmentAccounts?.find((account) => account.accountStatus !== 'CLOSED');
    const accountStatus = activeAccount?.accountStatus ?? 'none';
    trackEvent('clicked_account_action_banner', {
      accountStatus,
      surface: 'editorial_action_banner',
      path: typeof window === 'undefined' ? null : window.location.pathname,
    }).catch(captureTrackingError);

    if (!activeAccount) {
      window.open(secondaryUrl, '_blank');
      return;
    }
    if (activeAccount.accountStatus === 'FROZEN') {
      window.open(HELP_URL, '_blank');
      return;
    }
    window.open(`${investUrl}/account/portfolio`, '_blank');
  };

  return (
    <InFlow data-cy="editorial-action-banner">
      <BannerCarousel
        investmentAccount={investmentAccount}
        emailConfirmed={emailConfirmed}
        userEmail={typedUser?.email ?? ''}
        primaryUserId={typedUser?.primaryUserId ?? null}
        strapiBannerData={null}
        onAccountAction={handleAccountAction}
      />
    </InFlow>
  );
}
